import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Disc3, Calendar, Clock } from 'lucide-react';
import { differenceInDays } from 'date-fns';
import AnimatedSection from '@/components/ui/animated-section';
import { ForgedLine } from '@/components/ui/forged-line';

export function FeaturedRelease() {
  const { data: release } = useQuery({
    queryKey: ['featured-release'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('releases')
        .select('id, title, release_type, genre, release_date, cover_art_url, status, org_id')
        .eq('featured', true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  if (!release) return null;

  const daysUntil = release.release_date
    ? differenceInDays(new Date(release.release_date), new Date())
    : null;

  return (
    <section className="py-20 md:py-28 bg-card">
      <div className="container mx-auto px-4">
        <AnimatedSection animation="fade-up" as="div" className="text-center mb-10">
          <p className="text-sm font-medium text-accent uppercase tracking-[0.12em] mb-2">Featured Release</p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">Now Spinning</h2>
          <ForgedLine variant="divider" className="mt-4" />
        </AnimatedSection>

        <AnimatedSection animation="fade-up" as="div" delay={100}>
          <Card className="max-w-lg mx-auto overflow-hidden group">
            {release.cover_art_url ? (
              <div className="aspect-square w-full overflow-hidden relative">
                <img
                  src={release.cover_art_url}
                  alt={release.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-background/0 group-hover:bg-background/10 transition-colors duration-300" />
              </div>
            ) : (
              <div className="aspect-square w-full bg-background flex items-center justify-center">
                <Disc3 className="h-24 w-24 text-muted-foreground/30" />
              </div>
            )}
            <CardContent className="p-6 text-center space-y-2">
              <h3 className="text-xl font-heading font-bold text-foreground transition-transform duration-300 group-hover:-translate-y-1">
                {release.title}
              </h3>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <span className="capitalize">{release.release_type}</span>
                {release.genre && <span>· {release.genre}</span>}
              </div>
              {release.release_date && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Calendar className="h-3 w-3 text-accent" />
                  <span className="text-accent">{new Date(release.release_date).toLocaleDateString()}</span>
                  {daysUntil !== null && daysUntil > 0 && (
                    <Badge className="text-xs bg-accent/15 text-accent border border-accent/25 rounded-[4px]">
                      <Clock className="h-3 w-3 mr-1" />{daysUntil} days
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </AnimatedSection>
      </div>
    </section>
  );
}
